import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { 
  CalendarIcon, Upload, ImagePlus, Check, X, Loader2, 
  AlertCircle, Cloud
} from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UploadPage = () => {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  
  const [step, setStep] = useState(1);
  const [date, setDate] = useState(new Date());
  const [eventTitle, setEventTitle] = useState('');
  const [customTitle, setCustomTitle] = useState('');
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);
  const [uploadComplete, setUploadComplete] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files).slice(0, 30);
    setFiles(selectedFiles);
    
    // Generate previews
    const newPreviews = selectedFiles.map(file => ({
      name: file.name,
      url: URL.createObjectURL(file),
      size: file.size
    }));
    setPreviews(newPreviews);
  };

  const removeFile = (index) => {
    const newFiles = [...files];
    const newPreviews = [...previews];
    
    URL.revokeObjectURL(newPreviews[index].url);
    newFiles.splice(index, 1);
    newPreviews.splice(index, 1);
    
    setFiles(newFiles);
    setPreviews(newPreviews);
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    
    setUploading(true);
    setUploadProgress(files.map(f => ({ name: f.name, status: 'pending', progress: 0 })));
    setStep(3);

    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    formData.append('event_date', format(date, 'yyyy-MM-dd'));
    formData.append('event_title', eventTitle === 'custom' ? customTitle : eventTitle);
    if (selectedEvent) {
      formData.append('event_id', selectedEvent.id);
    }

    try {
      // Simulate individual progress updates
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => prev.map(item => {
          if (item.status === 'pending' && item.progress < 90) {
            return { ...item, progress: item.progress + Math.random() * 20 };
          }
          return item;
        }));
      }, 200);

      const response = await axios.post(`${API}/photos/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      clearInterval(progressInterval);

      // Update with actual results
      setUploadProgress(response.data.results.map(r => ({
        name: r.file_name,
        status: r.status,
        progress: 100,
        error: r.error
      })));

      setUploadComplete(true);
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadProgress(prev => prev.map(item => ({
        ...item,
        status: 'failed',
        error: error.response?.data?.detail || 'Upload failed'
      })));
    } finally {
      setUploading(false);
    }
  };

  const resetUpload = () => {
    setStep(1);
    setFiles([]);
    setPreviews([]);
    setUploadProgress([]);
    setUploadComplete(false);
    setEventTitle('');
    setCustomTitle('');
    setSelectedEvent(null);
    setDate(new Date());
  };

  const finalTitle = eventTitle === 'custom' ? customTitle : eventTitle;
  const canProceed = step === 1 
    ? (date && finalTitle && finalTitle.trim()) 
    : files.length > 0;

  return (
    <div className="pb-24" data-testid="upload-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 px-4 py-4 sticky top-0 z-10">
        <h1 className="font-serif text-2xl text-slate-900">Upload Photos</h1>
        <p className="text-slate-500 text-sm">Share your service memories</p>
      </div>

      <div className="p-4">
        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                step >= s 
                  ? 'bg-violet-600 text-white' 
                  : 'bg-slate-200 text-slate-500'
              }`}
            >
              {step > s ? <Check className="w-4 h-4" /> : s}
            </div>
          ))}
        </div>

        {/* Step 1: Service Details */}
        {step === 1 && (
          <div className="space-y-6 animate-fade-in">
            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <h2 className="font-serif text-lg text-slate-900 mb-4">Service Details</h2>
              
              {/* Date Picker */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  Service Date
                </label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      data-testid="date-picker-btn"
                      variant="outline"
                      className="w-full h-12 justify-start text-left font-normal rounded-2xl border-slate-200"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-slate-400" />
                      {date ? format(date, 'PPP') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Event Title */}
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  Event / Theme
                </label>
                <Select 
                  value={eventTitle} 
                  onValueChange={(val) => {
                    setEventTitle(val);
                    const event = events.find(e => e.title === val);
                    setSelectedEvent(event || null);
                  }}
                >
                  <SelectTrigger data-testid="event-select" className="h-12 rounded-2xl border-slate-200">
                    <SelectValue placeholder="Select or create event" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Sunday Service">Sunday Service</SelectItem>
                    <SelectItem value="Worship Night">Worship Night</SelectItem>
                    <SelectItem value="Youth Group">Youth Group</SelectItem>
                    <SelectItem value="Baptism">Baptism</SelectItem>
                    <SelectItem value="Community Event">Community Event</SelectItem>
                    {events.filter(e => !['Sunday Service', 'Worship Night', 'Youth Group', 'Baptism', 'Community Event'].includes(e.title)).map(event => (
                      <SelectItem key={event.id} value={event.title}>{event.title}</SelectItem>
                    ))}
                    <SelectItem value="custom">+ Custom title...</SelectItem>
                  </SelectContent>
                </Select>

                {eventTitle === 'custom' && (
                  <Input
                    data-testid="custom-title-input"
                    value={customTitle}
                    onChange={(e) => setCustomTitle(e.target.value)}
                    placeholder="Enter event title"
                    className="mt-3 h-12 rounded-2xl border-slate-200"
                  />
                )}
              </div>
            </div>

            <Button
              data-testid="continue-btn"
              onClick={() => setStep(2)}
              disabled={!canProceed}
              className="w-full h-14 rounded-full bg-violet-600 hover:bg-violet-700 text-white text-lg shadow-lg shadow-violet-200"
            >
              Continue
            </Button>
          </div>
        )}

        {/* Step 2: Photo Selection */}
        {step === 2 && (
          <div className="space-y-4 animate-fade-in">
            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif text-lg text-slate-900">Select Photos</h2>
                <span className="text-sm text-slate-500">{files.length}/30</span>
              </div>

              {/* File Input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/heic,image/heif"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />

              {files.length === 0 ? (
                <button
                  data-testid="select-photos-btn"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full aspect-video bg-slate-50 border-2 border-dashed border-slate-200 rounded-2xl flex flex-col items-center justify-center gap-3 hover:border-violet-400 hover:bg-violet-50 transition-colors"
                >
                  <ImagePlus className="w-12 h-12 text-slate-400" />
                  <span className="text-slate-600 font-medium">Tap to select photos</span>
                  <span className="text-slate-400 text-sm">JPEG, PNG, HEIC • Max 30 photos</span>
                </button>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {previews.map((preview, index) => (
                      <div key={index} className="relative aspect-square">
                        <img
                          src={preview.url}
                          alt={preview.name}
                          className="w-full h-full object-cover rounded-xl"
                        />
                        <button
                          onClick={() => removeFile(index)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    {files.length < 30 && (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="aspect-square bg-slate-100 rounded-xl flex items-center justify-center hover:bg-violet-100 transition-colors"
                      >
                        <ImagePlus className="w-6 h-6 text-slate-400" />
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="flex-1 h-14 rounded-full border-slate-200"
              >
                Back
              </Button>
              <Button
                data-testid="upload-btn"
                onClick={handleUpload}
                disabled={files.length === 0}
                className="flex-1 h-14 rounded-full bg-violet-600 hover:bg-violet-700 text-white shadow-lg shadow-violet-200"
              >
                <Upload className="w-5 h-5 mr-2" />
                Upload {files.length} photo{files.length !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Upload Progress */}
        {step === 3 && (
          <div className="space-y-4 animate-fade-in">
            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif text-lg text-slate-900">
                  {uploadComplete ? 'Upload Complete' : 'Uploading...'}
                </h2>
                {!uploadComplete && (
                  <Loader2 className="w-5 h-5 animate-spin text-violet-600" />
                )}
              </div>

              {/* Background Upload Notice */}
              {!uploadComplete && (
                <div className="bg-violet-50 rounded-2xl p-4 mb-4 flex items-start gap-3">
                  <Cloud className="w-5 h-5 text-violet-600 mt-0.5" />
                  <p className="text-violet-700 text-sm">
                    Upload continues in the background — safe to lock your screen.
                  </p>
                </div>
              )}

              {/* Progress Items */}
              <div className="space-y-3 max-h-[50vh] overflow-y-auto">
                {uploadProgress.map((item, index) => (
                  <div key={index} className="upload-progress-item">
                    {previews[index] && (
                      <img 
                        src={previews[index].url} 
                        alt="" 
                        className="upload-thumbnail"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {item.name}
                      </p>
                      {item.status === 'pending' && (
                        <Progress value={item.progress} className="h-2 mt-2" />
                      )}
                      {item.error && (
                        <p className="text-xs text-red-500 mt-1">{item.error}</p>
                      )}
                    </div>
                    <div className="flex-shrink-0">
                      {item.status === 'success' && (
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <Check className="w-4 h-4 text-green-600" />
                        </div>
                      )}
                      {item.status === 'failed' && (
                        <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                          <X className="w-4 h-4 text-red-600" />
                        </div>
                      )}
                      {item.status === 'pending' && (
                        <Loader2 className="w-5 h-5 animate-spin text-violet-600" />
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary */}
              {uploadComplete && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">
                      {uploadProgress.filter(p => p.status === 'success').length} of {uploadProgress.length} uploaded
                    </span>
                    {uploadProgress.some(p => p.status === 'failed') && (
                      <span className="text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        Some uploads failed
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {uploadComplete && (
              <Button
                data-testid="upload-more-btn"
                onClick={resetUpload}
                className="w-full h-14 rounded-full bg-violet-600 hover:bg-violet-700 text-white text-lg shadow-lg shadow-violet-200"
              >
                Upload More Photos
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadPage;
